// VideoWatch.tsx - COMPLETO E CORRIGIDO
import FeatherIcon from 'feather-icons-react';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import {
  Alert,
  Button,
  Card,
  Col,
  Container,
  Row,
  Spinner
} from 'react-bootstrap';
import { useNavigate, useSearchParams } from 'react-router-dom';

import MapComponent, { TrailDisplayMode } from 'components/common/MapComponent';
import ImageViewer, { ImageViewerRef } from 'components/video/ImageViewer';
import KeyboardShortcutsHelp from 'components/video/KeyboardShortcutsHelp';
import ProjectInfo from 'components/video/ProjectInfo';
import ProjectOverlaySelector from 'components/video/ProjectOverlaySelector';
import SignalErrorBoundary from 'components/video/SignalErrorBoundary';
import SignalFormModal from 'components/video/SignalFormModal';
import SignalOverlayViewModal from 'components/video/SignalOverlayViewModal';
import SignalsList from 'components/video/SignalsList';
import VideoControls from 'components/video/VideoControls';

import { useGPSDisplacement } from 'hooks/useGPSDisplacement';
import { useImagePlayer } from 'hooks/useImagePlayer';
import { useOverlaySigns } from 'hooks/useOverlaySigns';
import { useSignalMarkers } from 'hooks/useSignalMarkers';
import { useVideoProject } from 'hooks/useVideoProject';

import { ProjectImage } from 'services/videoService';
import { getServiceName } from 'utils/signImageFilters';

// Error Boundary Component (inline)

const VideoWatch: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const imageViewerRef = useRef<ImageViewerRef>(null);
  const [showTrailPoints, setShowTrailPoints] = useState(true);
  const [showSignImages, setShowSignImages] = useState(true);
  const [showSignImagesInViewer, setShowSignImagesInViewer] = useState(true);
  const [mapClickEnabled, setMapClickEnabled] = useState(false);

  // Estados para overlay de projetos
  const [selectedOverlayProjectIds, setSelectedOverlayProjectIds] = useState<
    number[]
  >([]);
  const [overlayProximityRadius, setOverlayProximityRadius] = useState(20);

  // Estados para modal de overlay
  const [showOverlayModal, setShowOverlayModal] = useState(false);
  const [selectedOverlaySign, setSelectedOverlaySign] = useState<any>(null);
  const [selectedOverlayMarkerId, setSelectedOverlayMarkerId] = useState<
    string | null
  >(null);

  // Estado para modo de visualização da trilha
  const [trailDisplayMode, setTrailDisplayMode] =
    useState<TrailDisplayMode>('window');

  const {
    project,
    images,
    currentImage,
    loading: projectLoading,
    error: projectError,
    setCurrentImage
  } = useVideoProject(searchParams.get('projectId'));

  const {
    currentImageIndex,
    isPlaying,
    playbackSpeed,
    handlePause,
    togglePlayPause,
    changePlaybackSpeed,
    navigateFrames,
    goToFrame,
    handleImageChange
  } = useImagePlayer({
    images,
    initialIndex: 0
  });

  const {
    savedSigns,
    visibleSigns,
    showFormModal,
    editingIndex,
    formData,
    imageClickEnabled,
    dragModeEnabled,
    clickedCoordinates,
    showMarker,
    selectedSignIndex,
    isBlinking,
    draggingSignIndex,
    dragCoordinates,
    mapDragModeEnabled,
    draggingMapSignId,
    mapDragCoordinates,
    setShowFormModal,
    setEditingIndex,
    setFormData,
    setImageClickEnabled,
    setDragModeEnabled,
    setMapDragModeEnabled,
    handleFormSubmit,
    handleEditSign,
    handleDeleteSign,
    handleSelectSign,
    handleMarkerMouseDown,
    handleMarkerMouseMove,
    handleMarkerMouseUp,
    handleMapSignDragStart,
    handleMapSignDragMove,
    handleMapSignDragEnd
  } = useSignalMarkers(project, currentImage, images, imageViewerRef);

  // Função de cleanup mais robusta
  const cleanupModalStates = useCallback(() => {
    setShowFormModal(false);
    setEditingIndex(null);
    setMapClickEnabled(false);

    // Limpar qualquer resultado GPS temporário
    if ((window as any).__clickGPSResult) {
      delete (window as any).__clickGPSResult;
    }

    // Dar um pequeno delay para garantir que o DOM seja atualizado
    setTimeout(() => {
      // Force re-render se necessário
      setFormData(prevData => ({ ...prevData }));
    }, 100);
  }, [setShowFormModal, setEditingIndex, setFormData]);

  // Modificar handleFormSubmit para usar a função de cleanup
  const handleFormSubmitWithCleanup = useCallback(
    async (data: any) => {
      try {
        await handleFormSubmit(data);
        // Pequeno delay para evitar conflitos de DOM
        await new Promise(resolve => setTimeout(resolve, 100));
        cleanupModalStates();
      } catch (error) {
        console.error('Erro ao salvar sinalização:', error);
        cleanupModalStates();
      }
    },
    [handleFormSubmit, cleanupModalStates]
  );

  useEffect(() => {
    if (images.length > 0 && images[currentImageIndex]) {
      setCurrentImage(images[currentImageIndex]);
    }
  }, [currentImageIndex, images, setCurrentImage]);

  useEffect(() => {
    const handleNavigateToImage = (e: Event) => {
      const customEvent = e as CustomEvent;
      const { imageIndex } = customEvent.detail;
      console.log(`[VideoWatch] 📡 Navegação recebida:`, { imageIndex });
      goToFrame(imageIndex);
    };

    window.addEventListener('navigateToImage', handleNavigateToImage);

    return () => {
      window.removeEventListener('navigateToImage', handleNavigateToImage);
    };
  }, [goToFrame]);

  useEffect(() => {
    if (!showFormModal) {
      console.log('[VideoWatch] Modal fechado');
      setMapClickEnabled(false);
    }
  }, [showFormModal]);

  // Auto-ajustar modo da trilha baseado no tamanho do projeto
  useEffect(() => {
    if (images.length > 0) {
      const newMode: TrailDisplayMode =
        images.length > 6000
          ? 'window'
          : images.length > 3000
          ? 'simplified'
          : 'all';

      console.log(
        `[VideoWatch] 🎯 Auto-ajustando modo da trilha: ${newMode} (${images.length} imagens)`
      );
      setTrailDisplayMode(newMode);
    }
  }, [images.length]);

  const handleImageClick = useCallback(() => {
    console.log('[VideoWatch] 🖱️ Clique (compatibilidade)');
  }, []);

  const handleMapClick = useCallback(
    (lat: number, lng: number) => {
      if (!mapClickEnabled || !currentImage) return;

      console.log('🗺️ Cadastrando placa no mapa:', { lat, lng });

      const mapGPSResult = {
        adjustedGPS: { lat, lng },
        originalGPS: { lat, lng },
        horizontalOffset: 0,
        depthOffset: 0,
        source: 'map_click',
        timestamp: Date.now()
      };

      (window as any).__clickGPSResult = mapGPSResult;

      setFormData({
        service_id: 1,
        signal_type: '',
        signal_device_id: 0,
        signal_film_id: 1,
        signal_support_type: '',
        signal_support_id: null,
        signal_regions_id: null, // ✅ ADICIONAR
        signal_region_area: null, // ✅ ADICIONAR
        comments: '',
        x: 0.5,
        y: 0.5,
        timestamp: currentImageIndex,
        frameNumber: currentImageIndex,
        imageId: currentImage.id,
        lat: lat,
        long: lng
      });

      setMapClickEnabled(false);

      setTimeout(() => {
        setShowFormModal(true);
      }, 50);
    },
    [
      mapClickEnabled,
      currentImage,
      currentImageIndex,
      setFormData,
      setShowFormModal
    ]
  );

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement;
      if (
        target.tagName === 'INPUT' ||
        target.tagName === 'TEXTAREA' ||
        target.isContentEditable
      ) {
        return;
      }

      switch (e.key.toLowerCase()) {
        case 'escape':
          if (mapClickEnabled) {
            console.log('[VideoWatch] ESC - desabilitando modo mapa');
            setMapClickEnabled(false);
          } else if (selectedOverlayMarkerId !== null) {
            console.log('[VideoWatch] ESC - desmarcando overlay');
            setSelectedOverlayMarkerId(null);
          } else if (selectedSignIndex !== null) {
            console.log('[VideoWatch] ESC - desmarcando');
            handleSelectSign(null);
          }
          break;
        case ' ':
          e.preventDefault();
          console.log('[VideoWatch] ESPAÇO - Play/Pause');
          togglePlayPause();
          break;

        case 'a':
        case 'arrowleft':
          e.preventDefault();
          console.log('[VideoWatch] A/← - Voltar 1 frame');
          navigateFrames(-1);
          break;

        case 'd':
        case 'arrowright':
          e.preventDefault();
          console.log('[VideoWatch] D/→ - Avançar 1 frame');
          navigateFrames(1);
          break;

        case 'w':
        case 'arrowup':
          e.preventDefault();
          console.log('[VideoWatch] W/↑ - Voltar 5 frames');
          navigateFrames(-5);
          break;

        case 's':
        case 'arrowdown':
          e.preventDefault();
          console.log('[VideoWatch] S/↓ - Avançar 5 frames');
          navigateFrames(5);
          break;

        case '+':
        case '=':
          if (e.ctrlKey || e.metaKey) {
            e.preventDefault();
            console.log('[VideoWatch] Ctrl + + - Zoom In');
            window.dispatchEvent(new CustomEvent('zoomIn'));
          }
          break;

        case '-':
        case '_':
          if (e.ctrlKey || e.metaKey) {
            e.preventDefault();
            console.log('[VideoWatch] Ctrl + - - Zoom Out');
            window.dispatchEvent(new CustomEvent('zoomOut'));
          }
          break;

        case '0':
          if (e.ctrlKey || e.metaKey) {
            e.preventDefault();
            console.log('[VideoWatch] Ctrl + 0 - Reset Zoom');
            window.dispatchEvent(new CustomEvent('resetZoom'));
          }
          break;

        default:
          break;
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [
    selectedSignIndex,
    selectedOverlayMarkerId,
    mapClickEnabled,
    handleSelectSign,
    togglePlayPause,
    navigateFrames
  ]);

  useEffect(() => {
    if (project && !imageClickEnabled) {
      console.log('[VideoWatch] ✅ Habilitando clique automático');
      setImageClickEnabled(true);
    }
  }, [project, imageClickEnabled, setImageClickEnabled]);

  const [gpsDisplacementEnabled, setGpsDisplacementEnabled] = useState(false);
  const [previousImage, setPreviousImage] = useState<ProjectImage | null>(null);

  const gpsDisplacement = useGPSDisplacement({
    currentImage,
    previousImage,
    videoRef: null as any,
    enabled: gpsDisplacementEnabled
  });

  // Hook para sinalizações overlay
  const {
    overlaySigns,
    loading: overlayLoading,
    error: overlayError
  } = useOverlaySigns({
    currentProjectId: project?.id || 0,
    selectedProjectIds: selectedOverlayProjectIds,
    enabled: selectedOverlayProjectIds.length > 0
  });

  // Debug: Log quando overlaySigns mudar
  useEffect(() => {
    console.log('📦 [DEBUG] overlaySigns atualizado:', {
      count: overlaySigns.length,
      signs: overlaySigns.map(s => ({
        id: s.id,
        projectId: s.projectId,
        projectName: s.projectName,
        markerId: `overlay_${s.projectId}_${s.id}`,
        hasSymbol: !!s.signal_device?.symbol_name
      }))
    });
  }, [overlaySigns]);

  useEffect(() => {
    if (overlayLoading) {
      console.log('[VideoWatch] 📦 Carregando sinalizações overlay...');
    }
    if (overlayError) {
      console.error('[VideoWatch] ❌ Erro ao carregar overlay:', overlayError);
    }
  }, [overlayLoading, overlayError]);

  const imageTrail = useMemo(() => {
    return images
      .filter(img => img.lat !== null && img.long !== null)
      .map(img => ({
        lat: img.lat!,
        lng: img.long!,
        id: img.id,
        filename: img.filename
      }));
  }, [images]);

  useEffect(() => {
    if (currentImage && images.length > 1) {
      const currentIndex = images.findIndex(img => img.id === currentImage.id);
      if (currentIndex > 0) {
        setPreviousImage(images[currentIndex - 1]);
      }
    }
  }, [currentImage, images]);

  const getImageIndexById = useCallback(
    (imageId: number): number => {
      const index = images.findIndex(img => img.id === imageId);
      if (index === -1) {
        console.error(`[VideoWatch] ❌ Image ID ${imageId} não encontrada`);
        return 0;
      }
      return index;
    },
    [images]
  );

  const handleMapMarkerClick = useCallback(
    (markerId: string) => {
      console.log('[VideoWatch] 🗺️ Marker do mapa clicado:', markerId);

      if (markerId.startsWith('trail_')) {
        const imageId = parseInt(markerId.replace('trail_', ''));
        const targetImageIndex = getImageIndexById(imageId);

        if (targetImageIndex !== -1) {
          console.log(
            `[VideoWatch] 🎯 Trail: navegando para imagem ${targetImageIndex}`
          );
          goToFrame(targetImageIndex);
        }
        return; // ⚠️ IMPORTANTE: return aqui para não processar outros handlers
      }

      // Detectar clique em marker overlay - SELECIONA E ABRE MODAL
      if (markerId.startsWith('overlay_')) {
        console.log(
          '[VideoWatch] 👁️ Clique em overlay - selecionando e abrindo modal'
        );

        // PRIMEIRO: Selecionar visualmente
        setSelectedOverlayMarkerId(markerId);

        // Extrair projectId e signId
        const parts = markerId.replace('overlay_', '').split('_');

        if (parts.length >= 2) {
          const projectId = parseInt(parts[0]);
          const signId = parseInt(parts[1]);

          console.log('[VideoWatch] 🔍 Buscando overlay sign:', {
            projectId,
            signId
          });

          // Buscar o sign completo
          const overlaySign = overlaySigns.find(
            s => s.projectId === projectId && s.id === signId
          );

          if (overlaySign) {
            console.log(
              '[VideoWatch] ✅ Overlay sign encontrado:',
              overlaySign
            );
            setSelectedOverlaySign(overlaySign);

            // DEPOIS: Abrir modal com delay para mostrar animação
            setTimeout(() => {
              setShowOverlayModal(true);
            }, 100);
          } else {
            console.error('[VideoWatch] ❌ Overlay sign não encontrado');
          }
        } else {
          console.error(
            '[VideoWatch] ❌ Formato de ID overlay inválido:',
            markerId
          );
        }

        return;
      }

      // Limpar seleção de overlay ao clicar em outros markers
      setSelectedOverlayMarkerId(null);

      // Duplo clique para markers normais (edit_sign_)
      if (markerId.startsWith('edit_sign_')) {
        const signId = parseInt(markerId.replace('edit_sign_', ''));
        const signIndex = savedSigns.findIndex(s => s.id === signId);
        if (signIndex !== -1) {
          handleEditSign(signIndex);
        }
        return;
      }

      // Primeiro clique para markers normais (sign_)
      if (markerId.startsWith('sign_')) {
        const signId = parseInt(markerId.replace('sign_', ''));
        const signIndex = savedSigns.findIndex(s => s.id === signId);

        if (signIndex !== -1) {
          const sign = savedSigns[signIndex];

          console.log('[VideoWatch] 📍 Sinalização:', {
            signIndex,
            signId: sign.id,
            project_image_id: sign.project_image_id
          });

          if (sign.project_image_id) {
            const targetImageIndex = getImageIndexById(sign.project_image_id);

            if (targetImageIndex !== -1) {
              console.log(
                `[VideoWatch] 🎯 Navegando para imagem ${targetImageIndex}`
              );
              goToFrame(targetImageIndex);

              setTimeout(() => {
                handleSelectSign(signIndex, 'map');
              }, 200);
            }
          }
        }
        return;
      }
    },
    [
      savedSigns,
      overlaySigns,
      handleEditSign,
      handleSelectSign,
      getImageIndexById,
      goToFrame
    ]
  );

  const enhancedMapMarkers = useMemo(() => {
    const markers: Array<{
      id: string;
      lat: number;
      lng: number;
      title: string;
      color?: string;
      icon?: string;
      type: 'current' | 'sign' | 'image';
      isSelected?: boolean;
      symbolName?: string;
      isOverlay?: boolean;
      projectName?: string;
      distance?: number;
      serviceId?: number; // ✅ ADICIONAR ESTA LINHA
    }> = [];

    if (
      currentImage &&
      currentImage.lat !== null &&
      currentImage.long !== null
    ) {
      markers.push({
        id: `current_${currentImage.id}`,
        lat: currentImage.lat,
        lng: currentImage.long,
        title: `Posição Atual: ${currentImage.filename}`,
        color: '#dc3545',
        type: 'current'
      });
    }

    // Sinalizações do projeto atual
    savedSigns.forEach((sign, index) => {
      const lat = sign.adjusted_lat || sign.lat;
      const lng = sign.adjusted_lng || sign.long;

      if (lat && lng) {
        let color = '#ffc107';
        if (sign.signal_device?.type === 'regulamentacao') {
          color = '#dc3545';
        } else if (sign.signal_device?.type === 'advertencia') {
          color = '#ffc107';
        } else if (sign.signal_device?.type === 'indicativa') {
          color = '#0d6efd';
        }

        markers.push({
          id: `sign_${sign.id}`,
          lat,
          lng,
          title: `${getServiceName(sign.service_id)}: ${
            sign.signal_device?.code || 'N/A'
          } - ${sign.signal_device?.name || 'Sinalização'}`,

          color,
          icon: String(index + 1),
          type: 'sign',
          isSelected: selectedSignIndex === index,
          symbolName: sign.signal_device?.symbol_name, // ✅ Mantém como vem da API
          serviceId: sign.service_id, // ✅ ADICIONAR ESTA LINHA
          isOverlay: false // ✅ ADICIONAR ESTA LINHA
        });
      }
    });

    // Sinalizações overlay de outros projetos
    overlaySigns.forEach(sign => {
      const lat = sign.adjusted_lat || sign.lat;
      const lng = sign.adjusted_lng || sign.long;

      if (lat && lng) {
        const markerId = `overlay_${sign.projectId}_${sign.id}`;

        console.log('📍 [DEBUG] Criando marker overlay:', {
          markerId,
          projectId: sign.projectId,
          signId: sign.id,
          lat,
          lng,
          symbolName: sign.signal_device?.symbol_name || '', // ✅ Mantém como vem da API
          isSelected: selectedOverlayMarkerId === markerId
        });

        markers.push({
          id: markerId,
          lat,
          lng,
          title: `[${sign.projectName}] ${
            sign.signal_device?.name || 'Sinalização'
          } - ${sign.signal_device?.code || 'N/A'}`,
          color: '#6c757d',
          icon: '◉',
          type: 'sign',
          isSelected: selectedOverlayMarkerId === markerId,
          symbolName: sign.signal_device?.symbol_name || '',
          isOverlay: true,
          projectName: sign.projectName,
          distance: sign.distance
        });
      }
    });

    return markers;
  }, [
    currentImage,
    savedSigns,
    overlaySigns,
    selectedSignIndex,
    selectedOverlayMarkerId
  ]);

  useEffect(() => {
    if (!showOverlayModal) {
      setSelectedOverlayMarkerId(null);
    }
  }, [showOverlayModal]);

  useEffect(() => {
    const token = localStorage.getItem('authToken');
    if (!token) {
      navigate('/');
    }
  }, [navigate]);

  useEffect(() => {
    if (!project || images.length === 0) return;

    const imageIdParam = searchParams.get('imageId');
    const frameParam = searchParams.get('frame');
    const signIdParam = searchParams.get('signId');

    console.log('[VideoWatch] 🔗 Deep Link:', {
      imageIdParam,
      frameParam,
      signIdParam,
      hasImages: images.length > 0,
      hasSigns: savedSigns.length > 0
    });

    if (!imageIdParam && !frameParam && !signIdParam) return;

    let targetFrame = currentImageIndex;

    if (imageIdParam) {
      const imageId = parseInt(imageIdParam);
      const imageIndex = images.findIndex(img => img.id === imageId);

      if (imageIndex !== -1) {
        console.log(
          `[VideoWatch] 📍 Navegando para imagem ID ${imageId} (frame ${imageIndex})`
        );
        targetFrame = imageIndex;
      } else {
        console.warn(`[VideoWatch] ⚠️ Imagem ID ${imageId} não encontrada`);
      }
    } else if (frameParam) {
      const frame = parseInt(frameParam);
      if (frame >= 0 && frame < images.length) {
        console.log(`[VideoWatch] 📍 Navegando para frame ${frame}`);
        targetFrame = frame;
      } else {
        console.warn(`[VideoWatch] ⚠️ Frame ${frame} fora do range`);
      }
    }

    if (targetFrame !== currentImageIndex) {
      goToFrame(targetFrame);
    }

    if (signIdParam) {
      const signId = parseInt(signIdParam);

      setTimeout(() => {
        const signIndex = savedSigns.findIndex(s => s.id === signId);

        if (signIndex !== -1) {
          console.log(
            `[VideoWatch] 🎯 Selecionando sinalização ID ${signId} (índice ${signIndex})`
          );
          handleSelectSign(signIndex);
        } else {
          console.warn(
            `[VideoWatch] ⚠️ Sinalização ID ${signId} não encontrada`
          );
        }
      }, 500);
    }

    if (imageIdParam || frameParam || signIdParam) {
      const newSearchParams = new URLSearchParams(searchParams);
      newSearchParams.delete('imageId');
      newSearchParams.delete('frame');
      newSearchParams.delete('signId');

      const newUrl = `${
        window.location.pathname
      }?${newSearchParams.toString()}`;
      window.history.replaceState({}, '', newUrl);
    }
  }, [
    project,
    images,
    savedSigns,
    searchParams,
    currentImageIndex,
    goToFrame,
    handleSelectSign
  ]);

  useEffect(() => {
    if (project) {
      document.title = `${project.name} - Visualizar Projeto`;
    }

    return () => {
      document.title = 'Visualizar Projeto';
    };
  }, [project]);

  const handleBackToProjects = () => {
    navigate('/app/video/projects');
  };

  const formatTimeWithMilliseconds = (frameNumber: number): string => {
    return `Frame ${frameNumber}`;
  };

  const getCurrentFrame = (): number => {
    return currentImageIndex;
  };

  if (projectLoading) {
    return (
      <Container fluid>
        <div className="text-center py-5">
          <Spinner animation="border" role="status">
            <span className="visually-hidden">Carregando...</span>
          </Spinner>
          <p className="mt-2 text-muted">Carregando projeto...</p>
        </div>
      </Container>
    );
  }

  if (projectError) {
    return (
      <Container fluid>
        <Row className="mb-3">
          <Col>
            <div className="d-flex justify-content-between align-items-center">
              <div>
                <h2 className="mb-0">
                  Visualizar Projeto
                  {project && (
                    <span className="text-primary"> - {project.name}</span>
                  )}
                </h2>
                {project?.description && (
                  <small className="text-muted d-block mt-1">
                    {project.description}
                  </small>
                )}
              </div>
              <Button
                variant="outline-secondary"
                onClick={handleBackToProjects}
              >
                <FeatherIcon icon="arrow-left" size={16} className="me-2" />
                Voltar
              </Button>
            </div>
          </Col>
        </Row>
        <Alert variant="danger">
          <FeatherIcon icon="alert-circle" size={16} className="me-2" />
          {projectError}
        </Alert>
      </Container>
    );
  }

  return (
    <Container fluid>
      <style>{`
        @keyframes pulse-marker {
          0%, 100% { 
            transform: translate(-50%, -50%) scale(1); 
            box-shadow: 0 2px 6px rgba(0,0,0,0.3);
          }
          50% { 
            transform: translate(-50%, -50%) scale(1.2); 
            box-shadow: 0 4px 12px rgba(0,0,0,0.5);
          }
        }
        
        @keyframes pulse-map {
          0%, 100% { 
            transform: scale(1); 
            box-shadow: 0 2px 6px rgba(0,0,0,0.3);
          }
          50% { 
            transform: scale(1.3); 
            box-shadow: 0 0 0 8px rgba(13, 110, 253, 0.3);
          }
        }
        
        .selected-marker {
          animation: pulse-marker 0.5s ease-in-out 4;
          border: 3px solid #0d6efd !important;
          z-index: 5 !important;
        }
        
        .selected-map-marker {
          animation: pulse-map 0.5s ease-in-out 4;
        }
      `}</style>

      <Row className="mb-3">
        <Col>
          <div className="d-flex justify-content-between align-items-center">
            <div>
              <h2 className="mb-0">
                Visualizar Projeto
                {project && (
                  <span className="text-primary"> - {project.name}</span>
                )}
              </h2>
              {project?.description && (
                <small className="text-muted d-block mt-1">
                  {project.description}
                </small>
              )}
            </div>
            <Button variant="outline-secondary" onClick={handleBackToProjects}>
              <FeatherIcon icon="arrow-left" size={16} className="me-2" />
              Voltar
            </Button>
          </div>
        </Col>
      </Row>

      {project && (
        <>
          <Row className="g-1">
            <Col lg={6} className="mb-3">
              <Card className="mb-3 h-100">
                <Card.Header>
                  <div className="d-flex justify-content-between align-items-center">
                    <div className="d-flex align-items-center gap-2">
                      <h6 className="mb-0">
                        <FeatherIcon icon="image" size={16} className="me-2" />
                        Imagens
                      </h6>

                      {savedSigns.length > 0 && (
                        <label
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            cursor: 'pointer',
                            margin: 0,
                            fontSize: '12px'
                          }}
                        >
                          <input
                            type="checkbox"
                            checked={showSignImagesInViewer}
                            onChange={() =>
                              setShowSignImagesInViewer(!showSignImagesInViewer)
                            }
                            style={{
                              marginRight: '6px',
                              cursor: 'pointer'
                            }}
                          />
                          <span>Imagens das placas</span>
                        </label>
                      )}
                    </div>

                    <div className="d-flex gap-1 align-items-center">
                      <KeyboardShortcutsHelp />
                      {selectedSignIndex !== null && (
                        <Button
                          variant="outline-primary"
                          size="sm"
                          onClick={() => handleSelectSign(null)}
                          title="Limpar seleção (ESC)"
                          className="p-1 me-2"
                          style={{ width: '32px', height: '32px' }}
                        >
                          <FeatherIcon icon="x-circle" size={14} />
                        </Button>
                      )}

                      <Button
                        variant={
                          gpsDisplacementEnabled ? 'info' : 'outline-secondary'
                        }
                        size="sm"
                        onClick={() => {
                          setGpsDisplacementEnabled(!gpsDisplacementEnabled);
                          if (gpsDisplacementEnabled) {
                            gpsDisplacement.reset();
                          }
                        }}
                        disabled={
                          !currentImage ||
                          currentImage.lat === null ||
                          currentImage.long === null
                        }
                        title={
                          gpsDisplacementEnabled
                            ? 'Desabilitar ajuste de GPS'
                            : 'Habilitar ajuste de GPS'
                        }
                        className="p-1"
                        style={{ width: '32px', height: '32px' }}
                      >
                        <FeatherIcon
                          icon={
                            gpsDisplacementEnabled ? 'crosshair' : 'map-pin'
                          }
                          size={14}
                        />
                      </Button>

                      <Button
                        variant={
                          imageClickEnabled ? 'success' : 'outline-secondary'
                        }
                        size="sm"
                        disabled={!currentImage}
                        onClick={() => setImageClickEnabled(!imageClickEnabled)}
                        title={
                          imageClickEnabled
                            ? 'Desabilitar clique para cadastrar'
                            : 'Habilitar clique para cadastrar'
                        }
                        className="p-1"
                        style={{ width: '32px', height: '32px' }}
                      >
                        <FeatherIcon icon="mouse-pointer" size={14} />
                      </Button>

                      <Button
                        variant={
                          dragModeEnabled ? 'warning' : 'outline-secondary'
                        }
                        size="sm"
                        disabled={!currentImage}
                        onClick={() => setDragModeEnabled(!dragModeEnabled)}
                        title={
                          dragModeEnabled
                            ? 'Desabilitar modo de arrastar marcadores'
                            : 'Habilitar modo de arrastar marcadores'
                        }
                        className="p-1"
                        style={{ width: '32px', height: '32px' }}
                      >
                        <FeatherIcon icon="move" size={14} />
                      </Button>
                    </div>
                  </div>
                </Card.Header>
                <Card.Body
                  className="p-0 h-100"
                  style={{ position: 'relative' }}
                >
                  {draggingSignIndex !== null && dragCoordinates && (
                    <div
                      style={{
                        position: 'absolute',
                        top: '10px',
                        left: '10px',
                        zIndex: 1000,
                        backgroundColor: 'rgba(255, 193, 7, 0.95)',
                        color: '#000',
                        padding: '8px 12px',
                        borderRadius: '6px',
                        boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
                        fontSize: '13px',
                        fontWeight: '600',
                        fontFamily: 'monospace',
                        pointerEvents: 'none'
                      }}
                    >
                      <div style={{ marginBottom: '2px' }}>
                        🎯 Movendo marcador
                      </div>
                      <div style={{ fontSize: '11px' }}>
                        X: {dragCoordinates.x.toFixed(4)}
                        <br />
                        Y: {dragCoordinates.y.toFixed(4)}
                      </div>
                    </div>
                  )}

                  <SignalErrorBoundary>
                    <ImageViewer
                      ref={imageViewerRef}
                      projectId={project.id}
                      images={images}
                      currentImageIndex={currentImageIndex}
                      isPlaying={isPlaying}
                      playbackSpeed={playbackSpeed}
                      visibleSigns={visibleSigns}
                      savedSigns={savedSigns}
                      selectedSignIndex={selectedSignIndex}
                      isBlinking={isBlinking}
                      onMarkerClick={handleSelectSign}
                      clickedCoordinates={clickedCoordinates}
                      showMarker={showMarker}
                      videoClickEnabled={imageClickEnabled}
                      dragModeEnabled={dragModeEnabled}
                      gpsDisplacementEnabled={gpsDisplacementEnabled}
                      gpsDisplacement={gpsDisplacement}
                      cameraBearing={currentImage?.bearing ?? null}
                      onImageChange={handleImageChange}
                      onPause={handlePause}
                      onImageClick={handleImageClick}
                      onMarkerMouseDown={handleMarkerMouseDown}
                      onMarkerMouseMove={handleMarkerMouseMove}
                      onMarkerMouseUp={handleMarkerMouseUp}
                      onEditSign={handleEditSign}
                      showSignImages={showSignImagesInViewer}
                    />
                  </SignalErrorBoundary>
                </Card.Body>
              </Card>
            </Col>

            <Col lg={6} className="mb-3">
              <Card className="h-100 d-flex flex-column">
                <Card.Header>
                  <div className="d-flex justify-content-between align-items-center">
                    <h6 className="mb-0">
                      <FeatherIcon icon="map-pin" size={16} className="me-2" />
                      Mapa de Localização
                    </h6>

                    <div className="d-flex align-items-center gap-2">
                      <Button
                        variant={
                          mapClickEnabled ? 'success' : 'outline-success'
                        }
                        size="sm"
                        onClick={() => setMapClickEnabled(!mapClickEnabled)}
                        disabled={!currentImage}
                        title={
                          mapClickEnabled
                            ? 'Cancelar cadastro no mapa (ESC)'
                            : 'Cadastrar placa clicando no mapa'
                        }
                        style={{
                          fontSize: '11px',
                          padding: '4px 8px',
                          fontWeight: mapClickEnabled ? '600' : '500'
                        }}
                      >
                        <FeatherIcon
                          icon="plus-circle"
                          size={12}
                          className="me-1"
                        />
                        {mapClickEnabled ? 'Cancelar' : 'Adicionar'}
                      </Button>

                      <Button
                        variant={
                          mapDragModeEnabled ? 'warning' : 'outline-warning'
                        }
                        size="sm"
                        onClick={() =>
                          setMapDragModeEnabled(!mapDragModeEnabled)
                        }
                        disabled={savedSigns.length === 0}
                        title={
                          mapDragModeEnabled
                            ? 'Desabilitar modo de arrastar GPS no mapa'
                            : 'Habilitar modo de arrastar GPS no mapa'
                        }
                        className="p-1"
                        style={{ width: '32px', height: '32px' }}
                      >
                        <FeatherIcon icon="move" size={14} />
                      </Button>

                      {project && (
                        <div style={{ position: 'relative' }}>
                          <ProjectOverlaySelector
                            currentProjectId={project.id}
                            selectedProjectIds={selectedOverlayProjectIds}
                            onProjectsChange={setSelectedOverlayProjectIds}
                            proximityRadius={overlayProximityRadius}
                            onProximityRadiusChange={setOverlayProximityRadius}
                          />
                          {overlayLoading && (
                            <div
                              style={{
                                position: 'absolute',
                                top: '-8px',
                                right: '-8px',
                                width: '20px',
                                height: '20px',
                                borderRadius: '50%',
                                backgroundColor: '#0d6efd',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
                              }}
                            >
                              <Spinner
                                animation="border"
                                size="sm"
                                style={{
                                  width: '12px',
                                  height: '12px',
                                  borderWidth: '2px',
                                  color: 'white'
                                }}
                              />
                            </div>
                          )}
                        </div>
                      )}

                      {imageTrail.length > 0 && (
                        <>
                          <label
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              cursor: 'pointer',
                              margin: 0,
                              fontSize: '12px'
                            }}
                          >
                            <input
                              type="checkbox"
                              checked={showTrailPoints}
                              onChange={() =>
                                setShowTrailPoints(!showTrailPoints)
                              }
                              style={{
                                marginRight: '6px',
                                cursor: 'pointer'
                              }}
                            />
                            <span>Pontos da trilha ({imageTrail.length})</span>
                          </label>

                          {showTrailPoints && (
                            <select
                              value={trailDisplayMode}
                              onChange={e =>
                                setTrailDisplayMode(
                                  e.target.value as TrailDisplayMode
                                )
                              }
                              style={{
                                fontSize: '11px',
                                padding: '2px 4px',
                                borderRadius: '4px',
                                border: '1px solid #dee2e6',
                                cursor: 'pointer',
                                backgroundColor: 'white'
                              }}
                              title="Modo de exibição da trilha"
                            >
                              <option value="window">🪟 Janela</option>
                              <option value="simplified">
                                ⚡ Simplificado
                              </option>
                              <option value="all">🌐 Todos</option>
                              <option value="none">❌ Nenhum</option>
                            </select>
                          )}
                        </>
                      )}

                      {savedSigns.length > 0 && (
                        <label
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            cursor: 'pointer',
                            margin: 0,
                            fontSize: '12px'
                          }}
                        >
                          <input
                            type="checkbox"
                            checked={showSignImages}
                            onChange={() => setShowSignImages(!showSignImages)}
                            style={{
                              marginRight: '6px',
                              cursor: 'pointer'
                            }}
                          />
                          <span>Imagens das placas</span>
                        </label>
                      )}
                    </div>
                  </div>
                </Card.Header>
                <Card.Body
                  className="p-0 flex-fill d-flex flex-column"
                  style={{ position: 'relative' }}
                >
                  {draggingMapSignId !== null && mapDragCoordinates && (
                    <div
                      style={{
                        position: 'absolute',
                        top: '10px',
                        left: '10px',
                        zIndex: 1000,
                        backgroundColor: 'rgba(255, 193, 7, 0.95)',
                        color: '#000',
                        padding: '8px 12px',
                        borderRadius: '6px',
                        boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
                        fontSize: '13px',
                        fontWeight: '600',
                        fontFamily: 'monospace',
                        pointerEvents: 'none'
                      }}
                    >
                      <div style={{ marginBottom: '2px' }}>🗺️ Movendo GPS</div>
                      <div style={{ fontSize: '11px' }}>
                        Lat: {mapDragCoordinates.lat.toFixed(6)}
                        <br />
                        Lng: {mapDragCoordinates.lng.toFixed(6)}
                      </div>
                    </div>
                  )}

                  <div className="flex-fill">
                    <MapComponent
                      //key={`map-${showSignImages}-${savedSigns.length}-${overlaySigns.length}`} // ✅ Força re-render
                      lat={currentImage?.lat ?? undefined}
                      lng={currentImage?.long ?? undefined}
                      title={
                        currentImage
                          ? `Localização Atual: ${currentImage.filename}`
                          : 'Localização do projeto'
                      }
                      markers={enhancedMapMarkers}
                      imageTrail={showTrailPoints ? imageTrail : []}
                      currentImageIndex={currentImageIndex}
                      onMarkerClick={handleMapMarkerClick}
                      onMapClick={handleMapClick}
                      mapClickEnabled={mapClickEnabled}
                      mapDragModeEnabled={mapDragModeEnabled}
                      draggingMapSignId={draggingMapSignId}
                      onMarkerDragStart={handleMapSignDragStart}
                      onMarkerDragMove={handleMapSignDragMove}
                      onMarkerDragEnd={handleMapSignDragEnd}
                      height="100%"
                      showZoomControls={true}
                      displacementResult={gpsDisplacement.displacementResult}
                      showDisplacementLine={
                        gpsDisplacementEnabled && gpsDisplacement.isActive
                      }
                      cameraBearing={currentImage?.bearing ?? null}
                      showTrailPoints={showTrailPoints}
                      onToggleTrailPoints={() =>
                        setShowTrailPoints(!showTrailPoints)
                      }
                      showSignImages={showSignImages}
                      onToggleSignImages={() =>
                        setShowSignImages(!showSignImages)
                      }
                      trailDisplayMode={trailDisplayMode}
                      onTrailDisplayModeChange={setTrailDisplayMode}
                      selectedSignMarkerId={
                        selectedSignIndex !== null
                          ? `sign_${savedSigns[selectedSignIndex]?.id}`
                          : null
                      }
                    />
                  </div>

                  {currentImage && (
                    <div className="p-2 bg-light border-top">
                      <div className="text-center">
                        <small className="text-muted d-block">
                          <strong>Frame:</strong> {currentImageIndex} /{' '}
                          {images.length - 1}
                        </small>
                        <small className="text-muted d-block">
                          <strong>Imagem:</strong> {currentImage.filename}
                        </small>
                      </div>
                    </div>
                  )}
                </Card.Body>
              </Card>
            </Col>
          </Row>

          <Row className="g-1">
            <Col lg={6} className="mb-3">
              <VideoControls
                playbackSpeed={playbackSpeed}
                isPlaying={isPlaying}
                dragModeEnabled={dragModeEnabled}
                gpsDisplacementEnabled={gpsDisplacementEnabled}
                gpsDisplacementResult={gpsDisplacement.displacementResult}
                onSpeedChange={changePlaybackSpeed}
                onNavigateFrames={navigateFrames}
                onTogglePlayPause={togglePlayPause}
                onToggleDragMode={() => setDragModeEnabled(!dragModeEnabled)}
                onToggleGPSDisplacement={() => {
                  setGpsDisplacementEnabled(!gpsDisplacementEnabled);
                  if (gpsDisplacementEnabled) {
                    gpsDisplacement.reset();
                  }
                }}
              />

              <ProjectInfo
                project={project}
                currentImage={currentImage}
                images={images}
                currentTime={currentImageIndex}
                duration={images.length}
                savedSigns={savedSigns}
                visibleSigns={visibleSigns}
                formatTime={formatTimeWithMilliseconds}
                getCurrentFrame={getCurrentFrame}
              />
            </Col>

            <Col lg={6} className="mb-3">
              <SignalsList
                savedSigns={savedSigns}
                images={images}
                onEdit={handleEditSign}
                onDelete={handleDeleteSign}
                onSelect={handleSelectSign}
                selectedIndex={selectedSignIndex}
                isBlinking={isBlinking}
                formatTime={formatTimeWithMilliseconds}
              />
            </Col>
          </Row>

          <SignalErrorBoundary
            onError={(error: Error) => {
              console.error('Erro no SignalFormModal:', error);
              cleanupModalStates();
            }}
          >
            <SignalFormModal
              show={showFormModal}
              onHide={() => {
                cleanupModalStates();
              }}
              onSubmit={handleFormSubmitWithCleanup}
              onDelete={
                editingIndex !== null
                  ? () => handleDeleteSign(editingIndex)
                  : undefined
              }
              formData={formData}
              setFormData={setFormData}
              currentImage={currentImage}
              editingIndex={editingIndex}
              formatTimeWithMilliseconds={formatTimeWithMilliseconds}
              gpsDisplacementEnabled={gpsDisplacementEnabled}
              hasGPSAdjustment={gpsDisplacement.displacementResult !== null}
              gpsDisplacementResult={
                gpsDisplacementEnabled && gpsDisplacement.displacementResult
                  ? gpsDisplacement.displacementResult
                  : (window as any).__clickGPSResult
              }
            />
          </SignalErrorBoundary>

          <SignalOverlayViewModal
            show={showOverlayModal}
            onHide={() => {
              console.log('[VideoWatch] 🚪 Fechando modal overlay');
              setShowOverlayModal(false);
              setSelectedOverlaySign(null);
            }}
            overlaySign={selectedOverlaySign}
          />
        </>
      )}
    </Container>
  );
};

export default VideoWatch;
